import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { ZONE_FIELD_IDS } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import {
  FC,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

interface ZoneData {
  id: string
  title: string
  zone_reference_image_id?: string
  drawing_bounds?: {
    x_start: number
    y_start: number
    x_end: number
    y_end: number
  }
  color?: string
  /** True while the zone is an unreviewed AI proposal (renders dashed). */
  ai_suggested?: boolean
  [key: string]: unknown
}

interface SmartImageViewerProps {
  url: string
  documentId: string
  height?: number
  enableZoneDrawing?: boolean
  onZoneDrawn?: (rect: {
    x_start: number
    y_start: number
    x_end: number
    y_end: number
  }) => void
  /**
   * Reports the browser-measured natural pixel size once the image loads,
   * stamped with this viewer's documentId so id and measurement always
   * describe the same image.
   */
  onNaturalSize?: (size: {
    documentId: string
    width: number
    height: number
  }) => void
  liveZones?: ZoneData[]
}

interface Point {
  x: number
  y: number
}

interface DrawingRect {
  start: Point
  end: Point
}

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.forecast.background2};
  border-radius: 4px;
  overflow: auto;
`

const ImageContainer = styled.div`
  position: relative;
  display: inline-block;
  max-width: 100%;
  max-height: 100%;
`

const StyledImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  background: white;
`

const SVGOverlay = styled.svg<{ $width: number; $height: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  pointer-events: none;
`

const InteractionLayer = styled.div<{
  $isDrawing: boolean
  $width: number
  $height: number
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  cursor: ${({ $isDrawing }) => ($isDrawing ? 'crosshair' : 'default')};
`

/**
 * Smart Image Viewer that automatically loads and displays zones for zone reference images
 * - Shows zones if they exist (from database)
 * - Optionally enables interactive zone drawing
 * - Works everywhere (not just in zones step)
 */
export const SmartImageViewer: FC<SmartImageViewerProps> = ({
  url,
  documentId,
  height = 1000,
  enableZoneDrawing = false,
  onZoneDrawn,
  onNaturalSize,
  liveZones,
}) => {
  // Load zones for this image directly from ticket (works in all steps)
  const { fieldValues } = useTicketAccess()

  // Use liveZones if provided (for real-time updates while editing), otherwise load from ticket
  const zonesForThisImage = useMemo(() => {
    if (liveZones) {
      return liveZones
    }

    const rawZones = unwrapFieldValue<ZoneData[]>(
      fieldValues[ZONE_FIELD_IDS.ZONES]
    )
    const zones = Array.isArray(rawZones) ? rawZones : []

    return zones.filter(
      (zone: ZoneData) => zone && zone.zone_reference_image_id === documentId
    )
  }, [liveZones, fieldValues, documentId])

  const [displayedSize, setDisplayedSize] = useState<{
    width: number
    height: number
  }>()
  const [naturalSize, setNaturalSize] = useState<{
    width: number
    height: number
  }>()
  const [drawingRect, setDrawingRect] = useState<DrawingRect>()
  const [isDrawing, setIsDrawing] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  const imageRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setImageLoaded(false)
    setNaturalSize(undefined)
    setDisplayedSize(undefined)
  }, [url])

  useEffect(() => {
    const updateDisplayedSize = () => {
      if (imageRef.current) {
        setDisplayedSize({
          width: imageRef.current.width,
          height: imageRef.current.height,
        })
      }
    }

    updateDisplayedSize()

    const resizeObserver = new ResizeObserver(updateDisplayedSize)
    if (imageRef.current) {
      resizeObserver.observe(imageRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [url])

  const getMousePosition = useCallback(
    (e: { clientX: number; clientY: number }): Point | null => {
      if (!imageRef.current || !naturalSize) return null

      const rect = imageRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      // Convert to natural image coordinates
      const naturalX = (x / rect.width) * naturalSize.width
      const naturalY = (y / rect.height) * naturalSize.height

      return {
        x: Math.max(0, Math.min(naturalX, naturalSize.width)),
        y: Math.max(0, Math.min(naturalY, naturalSize.height)),
      }
    },
    [naturalSize]
  )

  const handleMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      if (!enableZoneDrawing) return

      const point = getMousePosition(e)
      if (!point) return

      setIsDrawing(true)
      setDrawingRect({
        start: point,
        end: point,
      })
    },
    [enableZoneDrawing, getMousePosition]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDrawing || !drawingRect) return

      const point = getMousePosition(e)
      if (!point) return

      setDrawingRect({
        ...drawingRect,
        end: point,
      })
    },
    [isDrawing, drawingRect, getMousePosition]
  )

  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !drawingRect || !naturalSize || !onZoneDrawn) {
      setIsDrawing(false)
      setDrawingRect(undefined)
      return
    }

    const minSize = 20
    const width = Math.abs(drawingRect.end.x - drawingRect.start.x)
    const height = Math.abs(drawingRect.end.y - drawingRect.start.y)

    if (width >= minSize && height >= minSize) {
      onZoneDrawn({
        x_start: Math.min(drawingRect.start.x, drawingRect.end.x),
        y_start: Math.min(drawingRect.start.y, drawingRect.end.y),
        x_end: Math.max(drawingRect.start.x, drawingRect.end.x),
        y_end: Math.max(drawingRect.start.y, drawingRect.end.y),
      })
    }

    setIsDrawing(false)
    setDrawingRect(undefined)
  }, [isDrawing, drawingRect, naturalSize, onZoneDrawn])

  useEffect(() => {
    if (!isDrawing) return
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDrawing, handleMouseMove, handleMouseUp])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsDrawing(false)
        setDrawingRect(undefined)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const renderZone = useCallback(
    (zone: ZoneData, index: number) => {
      if (!zone.drawing_bounds || !displayedSize || !naturalSize) return null

      const bounds = zone.drawing_bounds
      const scale = displayedSize.width / naturalSize.width
      const x = bounds.x_start * scale
      const y = bounds.y_start * scale
      const width = (bounds.x_end - bounds.x_start) * scale
      const height = (bounds.y_end - bounds.y_start) * scale
      // Unreviewed AI proposals must be visually distinct from real zones —
      // dashed outline, lighter fill, and an explicit "(AI)" label — so the
      // SME can't mistake a pending draft for a confirmed decision.
      const isAiSuggested = !!zone.ai_suggested

      return (
        <g key={zone.id || index}>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill={zone.color || '#3B82F6'}
            fillOpacity={isAiSuggested ? 0.1 : 0.2}
            stroke={zone.color || '#3B82F6'}
            strokeWidth={2}
            strokeDasharray={isAiSuggested ? '6,4' : undefined}
            pointerEvents='none'
          />
          <text
            x={x + 5}
            y={y + 20}
            fill='#fff'
            fontSize='14'
            fontWeight='600'
            pointerEvents='none'
            style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
            {isAiSuggested ? `✦ ${zone.title} (AI)` : zone.title}
          </text>
        </g>
      )
    },
    [displayedSize, naturalSize]
  )

  const renderDrawingRect = useCallback(() => {
    if (!drawingRect || !displayedSize || !naturalSize) return null

    const scale = displayedSize.width / naturalSize.width
    const x = Math.min(drawingRect.start.x, drawingRect.end.x) * scale
    const y = Math.min(drawingRect.start.y, drawingRect.end.y) * scale
    const width = Math.abs(drawingRect.end.x - drawingRect.start.x) * scale
    const height = Math.abs(drawingRect.end.y - drawingRect.start.y) * scale

    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill='#3B82F6'
        fillOpacity={0.2}
        stroke='#3B82F6'
        strokeWidth={2}
        strokeDasharray='5,5'
        pointerEvents='none'
      />
    )
  }, [drawingRect, displayedSize, naturalSize])

  return (
    <Container style={{ height: height ? `${height}px` : '100%' }}>
      <ImageContainer ref={containerRef}>
        <StyledImage
          ref={imageRef}
          src={url}
          alt='Document with zones'
          onLoad={(e) => {
            const img = e.currentTarget
            const natural = {
              width: img.naturalWidth,
              height: img.naturalHeight,
            }
            setNaturalSize(natural)
            onNaturalSize?.({ documentId, ...natural })
            setDisplayedSize({ width: img.width, height: img.height })
            setImageLoaded(true)
          }}
          onError={(e) => {
            console.error('[SmartImageViewer] Image failed to load:', e)
          }}
          style={{ opacity: imageLoaded ? 1 : 0 }}
        />

        {displayedSize && naturalSize && imageLoaded && (
          <>
            <SVGOverlay
              $width={displayedSize.width}
              $height={displayedSize.height}
              width={displayedSize.width}
              height={displayedSize.height}>
              {zonesForThisImage.map((zone, index) => renderZone(zone, index))}
              {renderDrawingRect()}
            </SVGOverlay>

            {enableZoneDrawing && (
              <InteractionLayer
                $isDrawing={enableZoneDrawing}
                $width={displayedSize.width}
                $height={displayedSize.height}
                onMouseDown={handleMouseDown}
              />
            )}
          </>
        )}
      </ImageContainer>
    </Container>
  )
}
